import dayjs from 'dayjs';

import i18n from '@/i18n';
import {graphqlClient} from '@/services/graphqlClient';
import type {GetWorkTimeEntriesByDateRangeForExportQuery} from '@/services/graphqlHooks';
import {useGetWorkTimeEntriesByDateRangeForExportQuery} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';
import {formatWorkTimeDuration} from '@/shared/work-time/utils/formatWorkTimeDuration';
import workTimeEntryStatuses from '@/translation/workTimeEntryStatuses';
import {TIME_FORMATS} from '@/utils/timeFormats';

import DateRangeOptionsFields from '../../components/export-options/DateRangeOptionsFields';
import type {ExportDefinition} from '../../types/exportTypes';
import type {DateRangeOptions} from '../exportOptions';
import {
  dateRangeOptionsSchema,
  describeDateRange,
  formatExportDateTime,
  formatPersonName,
  getDateRangeDefaultOptions,
  getDateRangeFileName,
  isWithinDateRange,
} from '../exportOptions';

type WorkTimeExportRecord =
  GetWorkTimeEntriesByDateRangeForExportQuery['getWorkTimeEntriesByDateRange'][number] & {
    employeeName: string;
  };

export const workTimeExport: ExportDefinition<
  DateRangeOptions,
  WorkTimeExportRecord
> = {
  id: 'workTime',
  label: i18n.t('Work time'),
  permission: 'EXPORT_WORK_TIME',
  entitlement: 'WORK_TIME',
  optionsSchema: dateRangeOptionsSchema,
  OptionsFields: DateRangeOptionsFields,
  getDefaultOptions: getDateRangeDefaultOptions,
  load: async options => {
    const variables = {
      input: {
        startDate: dayjs(options.startDate)
          .subtract(1, 'day')
          .format(TIME_FORMATS.awsDate),
        endDate: dayjs(options.endDate)
          .add(1, 'day')
          .format(TIME_FORMATS.awsDate),
      },
    };
    const {getWorkTimeEntriesByDateRange, getAllUsers} =
      await queryClient.fetchQuery({
        queryKey:
          useGetWorkTimeEntriesByDateRangeForExportQuery.getKey(variables),
        queryFn: useGetWorkTimeEntriesByDateRangeForExportQuery.fetcher(
          graphqlClient,
          variables
        ),
      });
    const users = new Map(getAllUsers.map(user => [user.id, user.name]));

    return getWorkTimeEntriesByDateRange
      .filter(entry => isWithinDateRange(entry.startedAt, options))
      .map(entry => ({
        ...entry,
        employeeName: formatPersonName(users.get(entry.userId)) || entry.userId,
      }))
      .sort((entryA, entryB) => dayjs(entryA.startedAt).diff(entryB.startedAt));
  },
  columns: [
    {
      header: i18n.t('Employee'),
      filterable: true,
      value: entry => entry.employeeName,
    },
    {
      header: i18n.t('Started at'),
      value: entry => formatExportDateTime(entry.startedAt),
    },
    {
      header: i18n.t('Ended at'),
      value: entry => formatExportDateTime(entry.endedAt),
    },
    {
      header: i18n.t('Duration'),
      value: entry =>
        entry.totalMinutes === null || entry.totalMinutes === undefined
          ? ''
          : formatWorkTimeDuration(entry.totalMinutes),
    },
    {
      header: i18n.t('Status'),
      filterable: true,
      value: entry => (entry.status ? workTimeEntryStatuses[entry.status] : ''),
    },
    {header: i18n.t('Note'), value: entry => entry.note},
  ],
  describeOptions: describeDateRange,
  getFileName: options => getDateRangeFileName('work-time', options),
};
